import React, {
  useState, useEffect, useMemo, useCallback, useRef,
} from "react";
import {
  Modal, Button, Table, Spin, Radio, Tooltip, Input,
} from "antd";
import { toast } from "react-toastify";
import moment from "moment";
import dayjs from "dayjs";
import { isEmpty } from "lodash";
import {
  HistoryOutlined, EditOutlined, SwapOutlined, FilePdfOutlined,
} from "@ant-design/icons";
import { getUser } from "../../../../../utils/helpers";
import {
  markSampleQC,
  fetchTestHistoryQCData,
  fetchWoStages,
  fetchRelatedTests,
  checkTestSoftwareQCSpec,
  fetchRcaTracking,
  getRetestReasons,
} from "../../../../../actions/qc";
import useAbortController from "../../../../Common/hooks/useAbortController";
import ReportCommentModal from "../../../SendReportsServer/components/ReportCommentModal";
import { approveRejectSampleQC, updateTestResult } from "../../../../../actions/sendReports";
import {
  QC_OPTIONS, DEFAULT_ADDITIONAL_OPTIONS, userCorrectionMap,
} from "../../utils";
import { BATCH_REPORT_TEST_STATUS, REPORT_STATUS_CONFIG, MANUAL_QC_FLAGS } from "../../../../../utils/statusConfig";
import QCCell from "./QCCell";
// import SampleInfoSection from "./SampleInfoSection";
import QCFormSection from "./QCFormSection";
import RcaHistoryModal from "../../RcaHistoryModal";
import HistoryView from "./HistoryView";
import ConfirmationModal from "../../../../Common/ConfirmationModal";
import FilmImagePreviewModal from "./FilmImagePreviewModal";
import FilmThumbnail from "./FilmThumbnail";
import FapPdfPreviewModal from "./FapPdfPreviewModal";
import PendingTestsModal from "./PendingTestsModal";
import UnitConversionModal from "./UnitConversionModal";
import QcHistoryActivityModal from "../../QcHistory/QcHistoryActivityModal";
import StatusTag from "../../../../Common/components/StatusTag";
import AnnotateImageModal from "../../AnnotateImageModal/AnnotateImageModal";
import "./MarkSampleQCModal.css";
import { getAnnotationNameStem } from "../../AnnotateImageModal/utils";

const getPreferredFilmS3Info = (testData = {}) => (
  testData.ml_s3_info || testData.user_s3_info || null
);

const MarkSampleQCModal = ({
  open,
  onCancel,
  rowData: initialRowData,
  readOnly = false,
}) => {
  const user = useMemo(() => getUser(), []);
  const hasPickResultPermission = user.permissionMap.pick_result_qc;
  const hasEditResultPermission = user.permissionMap.edit_result_qc;
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyInfo, setHistoryInfo] = useState({});
  const [dateRange, setDateRange] = useState();
  const [rowData, setRowData] = useState(null);
  const [note, setNote] = useState();
  const [selectedResult, setSelectedResult] = useState("");
  const [, setAdditionalOptions] = useState(DEFAULT_ADDITIONAL_OPTIONS);
  const [selectedAdditionalOptions, setSelectedAdditionalOptions] = useState([]);
  const [generateRetestDisableReason, setGenerateRetestDisableReason] = useState("");

  const [reportCommentModalVisible, setReportCommentModalVisible] = useState(false);
  const [selectedActionOption, setSelectedActionOption] = useState(null);
  const [approveRejectLoading, setApproveRejectLoading] = useState(false);
  const [approveRejectData, setApproveRejectData] = useState(null);
  const [relatedTests, setRelatedTests] = useState(null);
  const [relatedWoInfo, setRelatedWoInfo] = useState(null);
  const [showRetestConfirmationModal, setShowRetestConfirmationModal] = useState(false);
  const [filmPreviewModalOpen, setFilmPreviewModalOpen] = useState(false);
  const [currentTestData, setCurrentTestData] = useState({});
  const [showPendingTestsModal, setShowPendingTestsModal] = useState(false);
  const [markSampleResponse, setMarkSampleResponse] = useState(null);
  const [selectedWorkorderForReporting, setSelectedWorkorderForReporting] = useState(null);
  const [isPickResultMode, setIsPickResultMode] = useState(false);
  const [validResultInput, setValidResultInput] = useState(true);
  const [resultUpdateLoading, setResultUpdateLoading] = useState(false);
  const [refetchRelatedInfoLoading, setRefetchRelatedInfoLoading] = useState(false);
  const [activityModalOpen, setActivityModalOpen] = useState(false);
  const [annotateImageModalOpen, setAnnotateImageModalOpen] = useState(false);
  const [unitConversionModalOpen, setUnitConversionModalOpen] = useState(false);
  const [unitConversionContext, setUnitConversionContext] = useState(null);
  const [retestComment, setRetestComment] = useState("");
  const [retestReasons, setRetestReasons] = useState([]);
  const [retestReasonsLoading, setRetestReasonsLoading] = useState(false);
  const [retestReasonsLoadError, setRetestReasonsLoadError] = useState("");
  const [retestReason, setRetestReason] = useState("");
  const currResultInputRef = useRef("");
  const [workordersEditState, setWorkordersEditState] = useState({});
  const [annotateImagePath, setAnnotateImagePath] = useState(null);
  const [specBlockReason, setSpecBlockReason] = useState(null);
  const [resultPdfModalOpen, setResultPdfModalOpen] = useState(false);
  const [currentResultPdfPreview, setCurrentResultPdfPreview] = useState({
    s3Info: null,
    title: "FAP PDF",
  });
  const [rcaRecord, setRcaRecord] = useState(null);
  const [expectedCompleteDate, setExpectedCompleteDate] = useState(null);
  const [pendingRcaS3Keys, setPendingRcaS3Keys] = useState([]);
  const [completeRcaChecked, setCompleteRcaChecked] = useState(false);
  const [rcaHistoryModalOpen, setRcaHistoryModalOpen] = useState(false);

  // Helper function to open pending tests modal
  const openPendingTestsModal = (response) => {
    setMarkSampleResponse(response);
    setShowPendingTestsModal(true);
  };

  const textareaRow = 2;

  const { setNewAbortController } = useAbortController();

  const formatDateRange = (input, format = "YYYY-MM-DD") => {
    if (!input || input.length < 2) return "";

    const startDate = moment(input[0].$d).format(format);
    const endDate = moment(input[1].$d).format(format);

    return `${startDate} - ${endDate}`;
  };

  const loadRcaTracking = async (tisPk, preferredRcaPk = null) => {
    if (!tisPk) {
      setRcaRecord(null);
      return;
    }
    const abortController = setNewAbortController();
    const {
      success, result, message, canceled,
    } = await fetchRcaTracking({
      tis_pk: tisPk,
      filter: "latest",
    }, abortController.signal);

    // Aborted (e.g. unmount / newer request) — skip all setState.
    if (canceled) {
      return;
    }

    if (success) {
      const records = Array.isArray(result) ? result : [];
      let latestRecord = records[0] || null;
      if (preferredRcaPk && records.length > 0) {
        const matched = records.find((item) => Number(item.id) === Number(preferredRcaPk));
        if (matched) {
          latestRecord = matched;
        }
      }
      setRcaRecord(latestRecord);
      const isActiveRca = latestRecord?.rca_status === 1;
      // Keep create-response rca_pk when Active; clear when none / Completed (re-open needs a new create).
      if (isActiveRca) {
        setSelectedResult("4");
        if (latestRecord.expected_complete_date) {
          const parsedExpectedDate = dayjs(latestRecord.expected_complete_date);
          if (parsedExpectedDate.isValid()) {
            setExpectedCompleteDate(parsedExpectedDate);
          }
        }
        if (latestRecord.manual_qc_status) {
          setNote(latestRecord.manual_qc_status);
        }
        if (Array.isArray(latestRecord.s3_keys) && latestRecord.s3_keys.length > 0) {
          setPendingRcaS3Keys(latestRecord.s3_keys);
        }
      }
    } else {
      toast.error(message);
      setRcaRecord(null);
    }
  };

  /** Update a test's qc result and qc status */
  const onMarkQC = async (newPendingTests = [], override_test_spec = false) => {
    setSpecBlockReason(null);
    setLoading(newPendingTests.length === 0); // Set loading to true if there are no pending tests. If pending tests are present, loading will be set to false because the onMarkQC will be called from the retest result edit popup
    const requiresQcReason = selectedResult === "2" || selectedResult === "3" || selectedResult === "4";
    if (requiresQcReason && (!note || !note.trim())) {
      toast.error("QC Reason is required for Hold, Retest, or RCA. Please enter a reason.");
      setLoading(false);
      return;
    }
    if (selectedResult === "3" && generateRetestDisableReason) {
      toast.error(generateRetestDisableReason || "Retest is not allowed for this sample.");
      setLoading(false);
      return;
    }
    if (selectedResult === "3" && !retestReason) {
      if (retestReasonsLoading) {
        toast.error("Retest reasons are still loading. Please wait.");
      } else if (retestReasons.length === 0) {
        toast.error(retestReasonsLoadError || "Failed to load retest reasons. Please try again.");
      } else {
        toast.error("Reason for Retest is required. Please select a reason.");
      }
      setLoading(false);
      return;
    }
    if (selectedResult === "3" && (!retestComment || !retestComment.trim())) {
      toast.error("Retest comment is required. Please enter a comment for the retest.");
      setLoading(false);
      return;
    }
    if (note && note.length > 500) {
      toast.error("QC Reason must be less than 500 characters");
      setLoading(false);
      return;
    }
    if (selectedResult === "4") {
      toast.error("RCA cannot be created or updated from QC Dashboard. Use Data Ops → RCA → RCA page.");
      setLoading(false);
      return;
    }
    if (selectedResult === "4" && !expectedCompleteDate) {
      toast.error("Expected RCA completion date is required.");
      setLoading(false);
      return;
    }
    if (selectedResult === "4" && expectedCompleteDate.isBefore(dayjs(), "day")) {
      toast.error("Expected RCA completion date must be today or a future date.");
      setLoading(false);
      return;
    }
    if (selectedResult === "4" && completeRcaChecked && pendingRcaS3Keys.length === 0) {
      toast.error("Upload at least one document before completing RCA.");
      setLoading(false);
      return;
    }
    const { tis_pk } = rowData;

    // Build the payload with all combined parameters
    const payload = {
      tis_pk,
      manual_qc_status: note,
      manual_qc_result: selectedResult,
      user_id: user.id,
      wo_id_pk: rowData.wo_id_pk,
      source: "QC Dashboard",
      sample_notes: selectedResult === "3" ? retestComment.trim() : "",
      ...(selectedResult === "3" && { retest_reason: retestReason }),
    };

    /*
    override_test_spec = true will override the test spec and allow the QC to be marked even if the test software QC is out of spec
    This is used when the user wants to mark the QC even if the test software QC is out of spec and the user has the permission to force pass the manual QC
    */
    if (override_test_spec) {
      payload.override_test_spec = true;
    }

    if (selectedAdditionalOptions.length > 0) {
      selectedAdditionalOptions.forEach((option) => {
        payload[option] = "True";
      });
    }

    // For Hold and Retest cases, always send the full test object
    if (selectedResult === "2" || selectedResult === "3") {
      payload.result = rowData;
    }

    if (selectedResult === "4") {
      payload.action = "create";
      payload.expected_complete_date = expectedCompleteDate.format("YYYY-MM-DD");
      // Attach docs on create when not completing in the same flow; complete path sends keys on complete.
      if (!completeRcaChecked && pendingRcaS3Keys.length > 0) {
        payload.s3_keys = pendingRcaS3Keys;
      }
    }

    // If there are pending tests, add the pending tests to the payload and the genista id list to the payload to create retest
    if (newPendingTests.length > 0) {
      payload.pending_tests = newPendingTests;
      payload.genista_id_list = markSampleResponse.genista_id_list;
    }

    // Send as a list of objects
    const response = await markSampleQC([payload]);
    const {
      success, message, status, pending_tests, genista_id_list, rca_pk,
    } = response;

    if (success) {
      setShowPendingTestsModal(false);
      const statusValue = status && (typeof status === "string") ? status.toLowerCase() : "";
      if (pending_tests && pending_tests.length > 0 && genista_id_list && genista_id_list.length > 0) {
        openPendingTestsModal(response);
      } else if (statusValue === "waiting_approval") {
        setShowRetestConfirmationModal(true);
      } else if (statusValue === "error_response") {
        toast.error(message);
      } else if (selectedResult === "4") {
        const createdRcaPk = rca_pk != null ? rca_pk : null;

        if (completeRcaChecked && createdRcaPk != null) {
          const completePayload = {
            tis_pk,
            wo_id_pk: rowData.wo_id_pk,
            user_id: user.id,
            manual_qc_result: "",
            action: "complete",
            rca_pk: createdRcaPk,
            manual_qc_status: note?.trim() || "",
            s3_keys: pendingRcaS3Keys,
            expected_complete_date: expectedCompleteDate.format("YYYY-MM-DD"),
            source: "QC Dashboard",
          };
          const completeResponse = await markSampleQC([completePayload]);
          if (completeResponse.success) {
            toast.success(completeResponse.message || "RCA created and completed successfully");
            onCancel(true);
          } else {
            toast.error(completeResponse.message || "RCA was created but could not be completed. Select RCA to finish.");
            setPendingRcaS3Keys([]);
            setCompleteRcaChecked(false);
            await loadRcaTracking(tis_pk, createdRcaPk);
          }
        } else if (completeRcaChecked && createdRcaPk == null) {
          toast.error("RCA was created but completion could not proceed (missing RCA id). Select RCA to finish.");
          setPendingRcaS3Keys([]);
          setCompleteRcaChecked(false);
          await loadRcaTracking(tis_pk);
        } else {
          toast.success(message || "RCA opened successfully");
          onCancel(true);
        }
      } else {
        toast.success(message);
        onCancel(true);
      }
    } else {
      toast.error(message);
    }
    setLoading(false);
  };

  const handleCheckTestSoftwareQCSpec = async () => {
    if (Number(selectedResult) !== 1) { // If the result is not Pass, then mark the QC without checking the test software QC spec
      onMarkQC();
      return;
    }
    setLoading(true);
    const params = {
      tis_pk: rowData.tis_pk,
    };
    const {
      success, result, message,
    } = await checkTestSoftwareQCSpec(params);

    setLoading(false);
    if (success) {
      const { spec_blocked, reason } = result;
      if (spec_blocked) {
        setSpecBlockReason(reason); // If the test software QC is out of spec, show the confirmation modal to the user
      } else {
        onMarkQC();
      }
    } else {
      toast.error(message);
    }
  };

  const fetchHistoryData = async () => {
    setHistoryLoading(true);
    const newAbortController = setNewAbortController();
    const {
      success, result, message, canceled,
    } = await fetchTestHistoryQCData({
      wo_id_pk: rowData.wo_id_pk,
      genista_id: rowData.genista_id,
      analysis: rowData.analysis,
      date_range: formatDateRange(dateRange),
    }, newAbortController.signal);
    if (success && result.samples) {
      setHistoryInfo({ ...result, samples: result.samples.map((item, key) => ({ ...item, key })) });
      setShowHistory(true);
    } else if (!canceled) {
      toast.error(message);
      setHistoryInfo({});
    }

    setHistoryLoading(canceled);
  };

  const disableCreateRetestOption = useCallback((reason = "Sample Prep stage is not marked") => {
    setGenerateRetestDisableReason(reason);
    setAdditionalOptions(DEFAULT_ADDITIONAL_OPTIONS.map((option) => {
      if (option.value === "generate_retest") {
        return { ...option, disabled: true };
      }
      return option;
    }));
  }, []);

  const fetchWoStagesData = useCallback(async () => {
    const { success, result, message } = await fetchWoStages(initialRowData.wo_id_pk);
    if (success) {
      const isMicroCategory = initialRowData.test_category === "Micro";
      // If a workorder has no samplePrep marked then disable the create retest option
      const disableCreateReest = result.filter((stage) => stage.id === 1 && stage.marked).length === 0 && isMicroCategory;
      if (disableCreateReest) {
        disableCreateRetestOption();
      }
    } else {
      toast.error(message);
      disableCreateRetestOption();
    }
  }, [initialRowData.wo_id_pk, initialRowData.test_category, disableCreateRetestOption]);

  const fetchRelatedTestsData = useCallback(async () => {
    const {
      analysis, genista_id, tis_pk, wo_id_pk: workorder_id_pk,
    } = initialRowData;

    const response = await fetchRelatedTests({
      workorder_id_pk,
      genista_id,
      tis_pk,
      analysis,
    });

    if (!response.success) {
      toast.error(response.message || "Failed to fetch related test data");
      setRelatedTests(null);
      setRelatedWoInfo(null);
      return;
    }

    const { result: apiResult } = response;

    const relatedTestsForCurrentWo = [];
    const workorderMap = new Map();
    const currentWorkorderTests = [];
    let currentRowFromApi = null;

    if (Array.isArray(apiResult)) {
      apiResult.forEach((item) => {
        const { workorder, customer, sample } = item;
        const tests = sample.tests;

        tests.forEach((test) => {
          const isCurrentWorkorder = workorder.pk === workorder_id_pk;

          // Flatten test into the structure expected by the modal table.
          const flatTest = {
            tis_pk: test.tis_pk,
            test_id_pk: test.id_pk,
            workorder_id: workorder.id,
            wo_id_pk: workorder.pk,
            customer_id: customer.id,
            customer_id_pk: customer.pk,
            company_name: customer.name,
            genista_id: sample.genista_id,
            sample_id: sample.sample_id,
            sample_type: sample.type,
            sample_notes: sample.notes,
            analysis: test.analysis,
            internal_name: test.analysis,
            test_category: test.category,
            result: test.result,
            test_default_value: test.default_value,
            result_activity: test.result_activity,
            result_status: test.status,
            generate_status: test.generate_status,
            // ML QC fields
            ml_qc_result: test.qc.ml.result,
            ml_qc_status: test.qc.ml.status,
            ml_qc_correction: test.qc.ml.correction,
            ml_qc_comments: test.qc.ml.comments,
            ml_correct_by: test.qc.ml.corrected_by,
            ml_correct_at: test.qc.ml.corrected_at,
            ml_qc_result_timestamp: test.qc.ml.timestamp,
            ml_soft_qc_result: test.qc.ml.ml_soft_qc_result,
            ml_soft_qc_status: test.qc.ml.ml_soft_qc_status,
            ml_soft_qc_timestamp: test.qc.ml.ml_soft_qc_timestamp,
            // Software QC fields
            qc_result: test.qc.software.result,
            qc_status: test.qc.software.status,
            qc_result_timestamp: test.qc.software.timestamp,
            // Manual QC fields
            manual_qc_result: test.qc.manual.result,
            manual_qc_status: test.qc.manual.status,
            manual_qc_by: test.qc.manual.manual_qc_by,
            manual_qc_at: test.qc.manual.qc_at,
            manual_qc_assigned_to: test.qc.manual.manual_qc_assigned_to,
            manual_qc_assigned_to_name: test.qc.manual.manual_qc_assigned_to_name,
            on_dashboard_since: test.on_dashboard_since,
            ml_s3_info: test.ml_s3_info,
            user_s3_info: test.user_s3_info,
            fap_s3_url_info: test.fap_s3_url_info ?? null,
            residual_solvents_s3_url_info: test.residual_solvents_s3_url_info ?? null,
            retest_reason: test.retest_reason,
          };

          if (isCurrentWorkorder) {
            relatedTestsForCurrentWo.push(flatTest);
            currentWorkorderTests.push(flatTest);
            if (flatTest.tis_pk != null && String(flatTest.tis_pk) === String(tis_pk)) {
              currentRowFromApi = flatTest;
            }
          }

          // Group by workorder pk — one main-table row per unique workorder
          // First test (tests[0]) becomes the main row; subsequent tests are only in the expanded view
          const woPk = workorder.pk;
          if (!workorderMap.has(woPk)) {
            workorderMap.set(woPk, {
              mainRow: flatTest,
              tests: [],
              is_retest: workorder.is_retest,
            });
          }
          workorderMap.get(woPk).tests.push(flatTest);
        });
      });
    }

    const workorders = [];
    workorderMap.forEach(({ mainRow, tests, is_retest }) => {
      workorders.push({ ...mainRow, is_retest, _allTests: tests });
    });

    setRelatedWoInfo(workorders);
    setRelatedTests(relatedTestsForCurrentWo);

    if (!currentRowFromApi) {
      return;
    }

    setRowData(currentRowFromApi);

    setNote(currentRowFromApi.manual_qc_status);
    setRetestComment(currentRowFromApi.sample_notes);
    setSelectedResult(() => {
      const initialValue = String(currentRowFromApi.manual_qc_result);
      const hasValueFromOptions = QC_OPTIONS.map((option) => option.value).includes(initialValue);
      return hasValueFromOptions ? initialValue : "";
    });
  }, [initialRowData]);

  useEffect(() => {
    if (rowData && showHistory) {
      fetchHistoryData();
    }
  }, [showHistory, dateRange, rowData]); // eslint-disable-line

  useEffect(() => {
    let isActive = true;
    (async () => {
      setLoading(true);
      fetchWoStagesData();
      await fetchRelatedTestsData();
      if (initialRowData?.tis_pk) {
        await loadRcaTracking(initialRowData.tis_pk);
      }
      if (isActive) {
        setLoading(false);
      }
    })();
    return () => {
      isActive = false;
      // Abort in-flight RCA (and any other request using this controller).
      setNewAbortController();
    };
  }, []); // eslint-disable-line

  const submitActiveRca = async () => {
    const activeRcaPk = rcaRecord?.id ?? null;
    if (!rowData?.tis_pk || activeRcaPk == null) {
      toast.error("Active RCA record is missing. Refresh and try again.");
      return;
    }
    if (!expectedCompleteDate) {
      toast.error("Expected RCA completion date is required.");
      return;
    }
    if (expectedCompleteDate.isBefore(dayjs(), "day")) {
      toast.error("Expected RCA completion date must be today or a future date.");
      return;
    }
    if (!note || !note.trim()) {
      toast.error("QC Reason is required for Hold, Retest, or RCA. Please enter a reason.");
      return;
    }
    if (note.length > 500) {
      toast.error("QC Reason must be less than 500 characters");
      return;
    }

    const existingKeys = Array.isArray(rcaRecord?.s3_keys) ? rcaRecord.s3_keys : [];
    const documentKeys = [
      ...existingKeys,
      ...pendingRcaS3Keys.filter((key) => !existingKeys.includes(key)),
    ];

    const hasRcaDocumentsForComplete = pendingRcaS3Keys.length > 0 || existingKeys.length > 0;
    if (completeRcaChecked && !hasRcaDocumentsForComplete) {
      toast.error("Upload at least one document before completing RCA.");
      return;
    }

    const currentExpectedDate = expectedCompleteDate.format("YYYY-MM-DD");
    const savedExpectedDate = rcaRecord?.expected_complete_date
      ? dayjs(rcaRecord.expected_complete_date).format("YYYY-MM-DD")
      : "";
    const savedRcaReason = (rcaRecord?.manual_qc_status || "").trim();
    const currentRcaReason = note.trim();
    const hasActiveRcaChange = currentExpectedDate !== savedExpectedDate
      || pendingRcaS3Keys.length > 0
      || currentRcaReason !== savedRcaReason
      || completeRcaChecked;

    if (!hasActiveRcaChange) {
      toast.error("Make a change before submitting RCA.");
      return;
    }

    setLoading(true);
    const payload = {
      tis_pk: rowData.tis_pk,
      wo_id_pk: rowData.wo_id_pk,
      user_id: user.id,
      action: completeRcaChecked ? "complete" : "update",
      rca_pk: activeRcaPk,
      expected_complete_date: currentExpectedDate,
      source: "QC Dashboard",
      // Complete clears QC result; update keeps RCA (4).
      manual_qc_result: completeRcaChecked ? "" : "4",
      manual_qc_status: note?.trim() || "",
    };

    if (completeRcaChecked && documentKeys.length > 0) {
      payload.s3_keys = documentKeys;
    } else if (pendingRcaS3Keys.length > 0) {
      payload.s3_keys = pendingRcaS3Keys;
    }

    const response = await markSampleQC([payload]);
    setLoading(false);

    if (response.success) {
      toast.success(
        response.message
        || (completeRcaChecked ? "RCA completed successfully" : "RCA updated successfully"),
      );
      onCancel(true);
    } else {
      toast.error(response.message || "Failed to update RCA");
    }
  };

  const handleRcaSubmit = () => {
    if (selectedResult === "4" && rcaRecord?.rca_status === 1) {
      submitActiveRca();
      return;
    }
    handleCheckTestSoftwareQCSpec();
  };

  useEffect(() => {
    if (selectedResult !== "3") {
      setRetestReasonsLoading(false);
      setRetestReasonsLoadError("");
      return undefined;
    }
    if (retestReasons.length > 0) return undefined;

    const controller = new AbortController();
    let requestIsActive = true;

    const fetchReasons = async () => {
      setRetestReasonsLoading(true);
      setRetestReasonsLoadError("");
      try {
        const res = await getRetestReasons(controller.signal);
        if (!requestIsActive || res.canceled) return;

        if (res.success) {
          const reasons = res.data ?? [];
          setRetestReasons(reasons);
          if (reasons.length === 0) {
            setRetestReasonsLoadError("No retest reasons available");
          }
        } else {
          const message = res.message || "Failed to load retest reasons";
          setRetestReasonsLoadError(message);
          toast.error(message);
        }
      } finally {
        if (requestIsActive) {
          setRetestReasonsLoading(false);
        }
      }
    };

    fetchReasons();
    return () => {
      requestIsActive = false;
      controller.abort();
    };
  }, [selectedResult]); // eslint-disable-line

  useEffect(() => {
    setAdditionalOptions((prevOptions) => prevOptions.map((option) => {
      if (option.value === "generate_retest") {
        if (option.disabled) {
          setGenerateRetestDisableReason("Sample Prep stage is not marked");
        } else {
          setGenerateRetestDisableReason("");
        }
        return option;
      }

      if (option.value === "contact_customer") {
        return { ...option, disabled: false };
      }

      return option;
    }));
  }, [isPickResultMode, selectedWorkorderForReporting]);

  const handleOpenFilmPreview = useCallback((testData) => {
    setCurrentTestData(testData);
    setFilmPreviewModalOpen(true);
  }, []);

  const handleCloseFilmPreview = () => {
    setFilmPreviewModalOpen(false);
    setCurrentTestData({});
  };

  const handleOpenResultPdf = useCallback((s3Info, title) => {
    setCurrentResultPdfPreview({ s3Info, title });
    setResultPdfModalOpen(true);
  }, []);

  const handleCloseResultPdf = () => {
    setResultPdfModalOpen(false);
    setCurrentResultPdfPreview({ s3Info: null, title: "FAP PDF" });
  };

  /**
   * Toggle icon visibility on hover for main table
   */
  const toggleWorkorderIcon = useCallback((index, action, iconType) => {
    setWorkordersEditState((prev) => ({
      ...prev,
      [index]: {
        ...prev[index],
        [iconType]: action === "enter",
      },
    }));
  }, []);

  /**
   * Handle Edit Result button click for main table
   */
  const handleEditWorkorderResult = useCallback((index, currentResult) => {
    setWorkordersEditState((prev) => ({
      ...prev,
      [index]: {
        ...prev[index],
        edit_result: true,
      },
    }));
    currResultInputRef.current = currentResult;
  }, []);

  /**
   * Handle change in result input value
   */
  const handleChangeResultValue = useCallback((e) => {
    const valid = e.target.value.length <= 2000;
    if (valid !== validResultInput) {
      setValidResultInput(valid);
    }
    currResultInputRef.current = e.target.value;
  }, [validResultInput]);

  /**
   * Handle Update Result for expanded table
   */
  const handleUpdateResult = useCallback(async (element, index) => {
    if (!currResultInputRef.current || currResultInputRef.current.trim() === "") {
      toast.warn("Please enter a valid Result value");
      return;
    }

    setResultUpdateLoading(true);
    const payload = {
      GenistaID: element.genista_id,
      Analysis: element.internal_name,
      Result: currResultInputRef.current.trim(),
      OrderID: element.workorder_id,
      overwrite_complete: false,
      source: "QC Dashboard Edit",
    };

    try {
      const response = await updateTestResult(payload);
      if (response.success) {
        toast.success(response.message);
        setRelatedTests((prev) => (prev && prev.length ? prev.map((t, i) => (i === index ? { ...t, edit_result: false } : t)) : prev));
        setRefetchRelatedInfoLoading(true);
        try {
          await fetchRelatedTestsData();
        } finally {
          setRefetchRelatedInfoLoading(false);
        }
        currResultInputRef.current = "";
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("An error occurred while updating the result");
    } finally {
      setResultUpdateLoading(false);
    }
  }, [fetchRelatedTestsData]);

  /**
   * Handle Update Result for main table
   */
  const handleUpdateWorkorderResult = useCallback(async (element, index) => {
    if (!currResultInputRef.current || currResultInputRef.current.trim() === "") {
      toast.warn("Please enter a valid Result value");
      return;
    }

    setResultUpdateLoading(true);
    const payload = {
      GenistaID: element.genista_id,
      Analysis: element.internal_name,
      Result: currResultInputRef.current.trim(),
      OrderID: element.workorder_id,
      overwrite_complete: false,
      source: "QC Dashboard Edit",
    };

    try {
      const response = await updateTestResult(payload);
      if (response.success) {
        toast.success(response.message);
        setWorkordersEditState((prev) => ({
          ...prev,
          [index]: { ...(prev[index] || {}), edit_result: false },
        }));
        setRefetchRelatedInfoLoading(true);
        try {
          await fetchRelatedTestsData();
        } finally {
          setRefetchRelatedInfoLoading(false);
        }
        currResultInputRef.current = "";
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("An error occurred while updating the result");
    } finally {
      setResultUpdateLoading(false);
    }
  }, [fetchRelatedTestsData]);

  /**
   * Reset edit state when modal closes
   */
  useEffect(() => {
    if (!open) {
      if (relatedTests) {
        setRelatedTests((prevTests) => {
          if (!prevTests) return prevTests;
          return prevTests.map((test) => ({
            ...test,
            edit_result: false,
            showEditResult: false,
          }));
        });
      }
      setWorkordersEditState({});
      currResultInputRef.current = "";
      setValidResultInput(true);
    }
  }, [open, relatedTests]);

  /**
   * Handle clicks outside edit container to cancel editing
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      const hasExpandedEdit = relatedTests && relatedTests.some((test) => test.edit_result);
      const hasWorkorderEdit = Object.values(workordersEditState).some((state) => state.edit_result);

      if (hasExpandedEdit || hasWorkorderEdit) {
        // Check if click is outside any edit container
        const editContainers = document.querySelectorAll(".MarkSampleQCModal__EditResultContainer");
        let isInsideEditContainer = false;
        editContainers.forEach((container) => {
          if (container.contains(event.target)) {
            isInsideEditContainer = true;
          }
        });

        if (!isInsideEditContainer) {
          // Cancel all active edits in expanded table
          if (hasExpandedEdit) {
            setRelatedTests((prevTests) => {
              if (!prevTests) return prevTests;
              return prevTests.map((test) => ({
                ...test,
                edit_result: false,
              }));
            });
          }
          // Cancel all active edits in main table
          if (hasWorkorderEdit) {
            setWorkordersEditState((prev) => {
              const newState = { ...prev };
              Object.keys(newState).forEach((key) => {
                if (newState[key].edit_result) {
                  newState[key] = { ...newState[key], edit_result: false };
                }
              });
              return newState;
            });
          }
          currResultInputRef.current = "";
        }
      }
    };

    const hasExpandedEdit = relatedTests && relatedTests.some((test) => test.edit_result);
    const hasWorkorderEdit = Object.values(workordersEditState).some((state) => state.edit_result);

    if (hasExpandedEdit || hasWorkorderEdit) {
      setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside);
      }, 0);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
    return undefined;
  }, [relatedTests, workordersEditState]);

  const handleShowHistory = () => {
    setShowHistory(true);
  };

  const handleBackToQC = () => {
    setShowHistory(false);
    setHistoryInfo({});
  };

  const onQCOptionChange = (e) => {
    const qcValue = e.target.value;
    // RCA radio is always disabled in UI; block selection defensively (Data Ops owns RCA).
    if (qcValue === "4") {
      toast.info("RCA is managed by Data Ops");
      return;
    }
    if (qcValue === "1") {
      setSelectedAdditionalOptions([]);
    }
    if (qcValue !== "4") {
      setCompleteRcaChecked(false);
      setPendingRcaS3Keys([]);
      setExpectedCompleteDate(null);
    }
    if (qcValue !== "3") {
      setRetestReason("");
    }
    setSelectedResult(qcValue);
  };

  /**
   * Handle workorder selection for reporting
   * Updates the selected workorder state
   */
  const handleWorkorderSelectionForReporting = useCallback((workorderKey) => {
    setSelectedWorkorderForReporting(workorderKey);
  }, []);

  /**
   * Handle Pick Result button click
   * Enables pick result mode to show radio buttons
   */
  const handlePickResult = useCallback(() => {
    setIsPickResultMode(true);
    setSelectedWorkorderForReporting(null); // Reset selection when entering pick mode
  }, []);

  /**
   * Handle Cancel button click in Pick Result mode
   * Exits pick result mode and resets selection
   */
  const handleCancelPickResult = useCallback(() => {
    setIsPickResultMode(false);
    setSelectedWorkorderForReporting(null);
  }, []);

  /**
   * Handle Save button click in Pick Result mode
   * Updates the result using the selected workorder's data
   */
  const handleSavePickResult = useCallback(async () => {
    if (!selectedWorkorderForReporting) {
      toast.warn("Please select a workorder result to save");
      return;
    }

    // Find the selected workorder by id (tis_pk) from main table so send-to-post API uses correct row
    let selectedWorkorder = null;
    if (relatedWoInfo) {
      if (Array.isArray(relatedWoInfo)) {
        const index = relatedWoInfo.findIndex((wo, idx) => {
          const key = (wo.tis_pk != null && wo.tis_pk !== "") ? String(wo.tis_pk) : `related-${wo.workorder_id || wo.tis_pk || idx}-${idx}`;
          return key === selectedWorkorderForReporting;
        });
        if (index !== -1) {
          selectedWorkorder = relatedWoInfo[index];
        }
      } else if (!isEmpty(relatedWoInfo)) {
        const key = (relatedWoInfo.tis_pk != null && relatedWoInfo.tis_pk !== "") ? String(relatedWoInfo.tis_pk) : `related-${relatedWoInfo.workorder_id || Date.now()}`;
        if (key === selectedWorkorderForReporting) {
          selectedWorkorder = relatedWoInfo;
        }
      }
    }

    if (!selectedWorkorder) {
      toast.error("Selected workorder not found");
      return;
    }

    if (!selectedWorkorder.result || selectedWorkorder.result === "-") {
      toast.warn("Selected workorder does not have a valid result");
      return;
    }

    setResultUpdateLoading(true);
    const payload = {
      ...(selectedWorkorder.tis_pk != null && selectedWorkorder.tis_pk !== "" && { tis_pk: selectedWorkorder.tis_pk }),
      GenistaID: selectedWorkorder.genista_id,
      Analysis: selectedWorkorder.internal_name,
      Result: selectedWorkorder.result,
      OrderID: selectedWorkorder.workorder_id,
      action: "Submit",
      overwrite_complete: false,
      source: "QC Dashboard Select",
    };

    try {
      const response = await updateTestResult(payload);
      if (response.success) {
        toast.success(response.message || "Result updated successfully");
        setRefetchRelatedInfoLoading(true);
        try {
          await fetchRelatedTestsData();
        } finally {
          setRefetchRelatedInfoLoading(false);
        }
        // Exit pick result mode after successful save
        setIsPickResultMode(false);
        setSelectedWorkorderForReporting(null);
      } else {
        toast.error(response.message || "Failed to update result");
      }
    } catch (error) {
      toast.error("An error occurred while updating the result");
    } finally {
      setResultUpdateLoading(false);
    }
  }, [selectedWorkorderForReporting, relatedWoInfo, fetchRelatedTestsData]);

  const handleApproveRejectSampleQC = async (sample, comment) => {
    const { id } = getUser();
    const {
      tis_pk,
      user_correction,
    } = sample;
    const payload = {
      tis_pk: String(tis_pk),
      user_correction: String(user_correction),
      user_id: id,
      ml_qc_comments: comment,
    };
    const response = await approveRejectSampleQC(payload);
    if (response.success) {
      const { message, result } = response;
      /**
       * There are two cases for success
       * 1. ML Result is not empty for the test, so backend will updates the user correction and return a object of result
       * 2. ML Result is empty for the test, backend will returns a empty result list
       */
      if (typeof result === "object" && result !== null && !Array.isArray(result)) {
        toast.success(message);
      } else {
        toast.info(message);
      }
    } else {
      toast.error(response.message);
    }
    return response;
  };

  /**
 * Update a test's user correction value
 */
  const updateSampleQCStatus = async (option, element, { comment }) => {
    setApproveRejectLoading(true);
    const newSample = {
      ...element,
      user_correction: userCorrectionMap[option],
    };
    const response = await handleApproveRejectSampleQC(newSample, comment);
    // If API succeeds and backend returns a valid result object, update the state
    if (response.success && typeof response.result === "object" && response.result !== null && !Array.isArray(response.result)) {
      const {
        corrected_by, corrected_at, user_correction, ml_qc_comments,
      } = response.result;

      // Update relatedTests if the element is in relatedTests
      setRelatedTests((prev) => {
        if (!prev || !Array.isArray(prev)) return prev;
        const newData = prev.map((item) => {
          if (item.tis_pk === element.tis_pk) {
            return {
              ...item,
              user_correction,
              ml_correct_by: corrected_by,
              ml_correct_at: corrected_at,
              ml_qc_comments,
            };
          }
          return item;
        });
        return newData;
      });
      // Update currentRowData if it's the current test
      if (rowData.tis_pk === element.tis_pk) {
        setRowData((prev) => ({
          ...prev,
          user_correction,
          ml_correct_by: corrected_by,
          ml_correct_at: corrected_at,
          ml_qc_comments,
        }));
      }
    }
    setApproveRejectData({ newSample, comment });
    setApproveRejectLoading(false);
  };

  const updateApproveReject = async (data) => {
    if (!selectedActionOption) return;
    const { selectedOption, element } = selectedActionOption;
    await updateSampleQCStatus(selectedOption, element, { comment: data });
    setSelectedActionOption(null);
    setReportCommentModalVisible(false);
  };

  const closeModal = async () => {
    // If user has approved/rejected
    if (approveRejectData) {
      setApproveRejectData(null);
      onCancel(true);
    } else {
      onCancel();
    }
  };

  const closeRetestConfirmationModal = () => {
    setShowRetestConfirmationModal(false);
    onCancel(true);
  };

  const getCommentModalTitle = useMemo(() => {
    if (!selectedActionOption) return "Add comment";
    const isApproval = selectedActionOption.selectedOption === "Approved";
    return `Add comment for ${isApproval ? "approval" : "rejection"}`;
  }, [selectedActionOption]);

  /**
   * Prepare workorders data for table display
   */
  const workordersData = useMemo(() => {
    const workorders = [];

    if (!rowData) {
      return workorders;
    }

    // Prepare current test data for expansion
    // API now always includes current test in relatedTests, so we can use it directly
    let testsToShow = [];
    if (relatedTests && relatedTests.length > 0) {
      testsToShow = relatedTests;
    } else if (rowData) {
      testsToShow = [rowData];
    }

    // Add related workorders if exists (handle both array and single object)
    if (relatedWoInfo) {
      // Handle array case
      if (Array.isArray(relatedWoInfo) && relatedWoInfo.length > 0) {
        relatedWoInfo.forEach((wo) => {
          const isCurrentWo = rowData.wo_id_pk === wo.wo_id_pk;
          const relatedWo = {
            key: String(wo.tis_pk),
            tis_pk: wo.tis_pk,
            workorder_id: wo.workorder_id,
            wo_id_pk: wo.wo_id_pk,
            customer_id: wo.customer_id,
            company_name: wo.company_name,
            genista_id: wo.genista_id,
            sample_id: wo.sample_id,
            sample_type: wo.sample_type,
            analysis: wo.analysis,
            internal_name: wo.internal_name,
            result: wo.result,
            result_activity: wo.result_activity,
            result_status: wo.result_status,
            generate_status: wo.generate_status,
            manual_qc_result: wo.manual_qc_result,
            manual_qc_by: wo.manual_qc_by,
            manual_qc_at: wo.manual_qc_at,
            manual_qc_assigned_to_name: wo.manual_qc_assigned_to_name,
            test_category: wo.test_category,
            isCurrent: isCurrentWo,
            relatedTests: isCurrentWo ? testsToShow : wo._allTests,
            is_retest: wo.is_retest,
            on_dashboard_since: wo.on_dashboard_since,
          };
          workorders.push(relatedWo);
        });
      } else if (!Array.isArray(relatedWoInfo) && !isEmpty(relatedWoInfo)) {
        const isCurrentWo = rowData.wo_id_pk === relatedWoInfo.wo_id_pk;
        const relatedWo = {
          key: String(relatedWoInfo.tis_pk),
          tis_pk: relatedWoInfo.tis_pk,
          workorder_id: relatedWoInfo.workorder_id,
          wo_id_pk: relatedWoInfo.wo_id_pk,
          customer_id: relatedWoInfo.customer_id,
          company_name: relatedWoInfo.company_name,
          genista_id: relatedWoInfo.genista_id,
          sample_id: relatedWoInfo.sample_id,
          sample_type: relatedWoInfo.sample_type,
          analysis: relatedWoInfo.analysis,
          internal_name: relatedWoInfo.internal_name,
          result: relatedWoInfo.result,
          result_activity: relatedWoInfo.result_activity,
          result_status: relatedWoInfo.result_status,
          generate_status: relatedWoInfo.generate_status,
          manual_qc_result: relatedWoInfo.manual_qc_result,
          manual_qc_by: relatedWoInfo.manual_qc_by,
          manual_qc_at: relatedWoInfo.manual_qc_at,
          manual_qc_assigned_to_name: relatedWoInfo.manual_qc_assigned_to_name,
          test_category: relatedWoInfo.test_category,
          isCurrent: isCurrentWo,
          relatedTests: isCurrentWo ? testsToShow : relatedWoInfo._allTests,
          is_retest: relatedWoInfo.is_retest,
          on_dashboard_since: relatedWoInfo.on_dashboard_since,
        };
        workorders.push(relatedWo);
      }
    }

    return workorders;
  }, [rowData, relatedTests, relatedWoInfo]);

  const retestReasonOptions = useMemo(
    () => (retestReasons || []).map((r) => ({ value: r.name, label: r.name })),
    [retestReasons],
  );

  /**
   * Check if all results are the same using Set
   * Returns true if all results are unique (different), false if all are same
   */
  const hasUniqueResults = useMemo(() => {
    if (workordersData.length <= 1) {
      return false; // Only one or no results, so not unique
    }

    // Create a Set of all results (excluding "-" as it's a placeholder)
    const resultSet = new Set(
      workordersData
        .map((wo) => wo.result)
        .filter((result) => result && result !== "-"),
    );

    // If Set size is 1, all results are the same
    // If Set size is > 1, results are different (unique)
    return resultSet.size > 1;
  }, [workordersData]);

  /**
   * True if any row in the workorders table has result_status === "Pending"
   */
  const hasAnyPendingResult = useMemo(() => workordersData.some(
    (wo) => wo.result_status && String(wo.result_status).toLowerCase() === "pending",
  ), [workordersData]);

  /**
   * Create a textarea for the test result with a Save button
   */
  const resultTextArea = useCallback((element, index, isWorkorder = false) => {
    const title = validResultInput ? "" : "You've exceeded the 2000-character limit.";

    return (
      <div className="MarkSampleQCModal__EditResultContainer">
        <Tooltip
          placement="left"
          title={title}
          open={title !== ""}
        >
          <Input.TextArea
            autoSize
            defaultValue={currResultInputRef.current}
            maxLength={300}
            onChange={handleChangeResultValue}
            status={title !== "" ? "error" : ""}
            className="MarkSampleQCModal__EditResultTextarea"
          />
        </Tooltip>
        <Button
          type="primary"
          size="small"
          className="MarkSampleQCModal__SaveResultBtn"
          disabled={title !== "" || resultUpdateLoading}
          loading={resultUpdateLoading}
          onClick={() => (isWorkorder ? handleUpdateWorkorderResult(element, index) : handleUpdateResult(element, index))}
        >
          Save
        </Button>
      </div>
    );
  }, [validResultInput, resultUpdateLoading, handleChangeResultValue, handleUpdateResult, handleUpdateWorkorderResult]);

  /**
   * Render QC cell for expanded table
   * Memoized to prevent unnecessary re-renders
   */
  const renderQCCell = useCallback((testData) => {
    const actionItems = readOnly ? [] : [
      { label: "Approve", key: "Approved" },
      { label: "Reject", key: "Rejected" },
    ];

    return (
      <QCCell
        originalTestData={rowData}
        testData={testData}
        user={user}
        actionItems={actionItems}
        readOnly={readOnly}
        onActionClick={(menuOption) => {
          const userCorrectionStatus = userCorrectionMap[testData.user_correction]
            || userCorrectionMap[testData.ml_qc_correction];
          setSelectedActionOption({
            selectedOption: menuOption.key,
            element: testData,
            currentStatus: userCorrectionStatus,
          });
          setReportCommentModalVisible(true);
        }}
      />
    );
  }, [readOnly, user, rowData, setSelectedActionOption, setReportCommentModalVisible]);

  // Memoize expanded columns to avoid recreation on every render
  const expandedColumns = useMemo(() => [
    {
      title: "Test",
      dataIndex: "analysis",
      key: "analysis",
      render: (text) => text,
    },
    {
      title: "Category",
      dataIndex: "test_category",
      key: "test_category",
      render: (text) => text,
    },
    {
      title: "Result",
      dataIndex: "result",
      key: "result",
      render: (text, record) => {
        const hasImage = getPreferredFilmS3Info(record) != null;
        return (
          <div className="MarkSampleQCModal__ResultCell">
            <span>{text}</span>
            {hasImage && (
              <FilmThumbnail
                onClick={() => handleOpenFilmPreview(record)}
              />
            )}
            {record.fap_s3_url_info && (
              <Tooltip title="Click to view FAP PDF">
                <FilePdfOutlined
                  onClick={() => handleOpenResultPdf(record.fap_s3_url_info, "FAP PDF")}
                  className="MarkSampleQCModal__FapPdfIcon"
                />
              </Tooltip>
            )}
            {record.residual_solvents_s3_url_info && (
              <Tooltip title="Click to view Residual Solvents PDF">
                <FilePdfOutlined
                  onClick={() => handleOpenResultPdf(
                    record.residual_solvents_s3_url_info,
                    "Residual Solvents PDF",
                  )}
                  className="MarkSampleQCModal__FapPdfIcon"
                />
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: "Spec",
      dataIndex: "test_default_value",
      key: "test_default_value",
      render: (text) => text,
    },
    {
      title: "QC",
      key: "qc",
      render: (_, testData) => renderQCCell(testData),
    },
  ], [renderQCCell, handleOpenFilmPreview, handleOpenResultPdf]);

  // Memoize expanded row render to prevent recreation on every expand/collapse
  const expandedRowRender = useCallback((record) => {
    // Get the latest tests data from state, not from the record snapshot
    let latestTests = [];
    if (record.isCurrent) {
      // API now always includes current test in relatedTests, so we can use it directly
      if (relatedTests && relatedTests.length > 0) {
        latestTests = relatedTests;
      } else if (rowData) {
        latestTests = [rowData];
      }
    } else {
      latestTests = record.relatedTests;
    }

    if (!latestTests || latestTests.length === 0) {
      return <div className="MarkSampleQCModal__NoTestsMessage">No tests available</div>;
    }

    const hasRetestReason = latestTests.some(
      (t) => t.retest_reason != null && String(t.retest_reason).trim() !== "",
    );

    const columns = hasRetestReason
      ? [
        ...expandedColumns,
        {
          title: "Retest Reason",
          dataIndex: "retest_reason",
          key: "retest_reason",
          render: (text) => text,
        },
      ]
      : expandedColumns;

    return (
      <Table
        columns={columns}
        dataSource={latestTests.map((test, index) => ({
          ...test,
          key: `test-${test.tis_pk || index}-${index}`,
        }))}
        pagination={false}
        size="small"
        bordered
        className="MarkSampleQCModal__ExpandedTable"
      />
    );
  }, [relatedTests, rowData, expandedColumns]);

  /**
   * Get tooltip message for Pick Result button based on disabling conditions
   */
  const getPickResultTooltipMessage = useMemo(() => {
    const hasMultipleResults = workordersData.length > 1;

    if (!hasMultipleResults) {
      return "Only one test result is available";
    }

    if (hasAnyPendingResult) {
      return "Pick Result is disabled when any test result is Pending";
    }

    if (!hasUniqueResults) {
      return "All results are the same";
    }

    return "";
  }, [workordersData, hasUniqueResults, hasAnyPendingResult]);
  // Memoize modal columns to avoid recreation on every render
  const modalColumns = useMemo(() => {
    const hasMultipleResults = workordersData.length > 1;
    const shouldShowPickResult = hasPickResultPermission && hasMultipleResults && hasUniqueResults && !hasAnyPendingResult;
    return [
      {
        title: "Customer ID",
        dataIndex: "customer_id",
        key: "customer_id",
        render: (text, record) => (
          <Tooltip placement="top" title={record.company_name}>
            <span>{text}</span>
          </Tooltip>
        ),
      },
      {
        title: "WO ID",
        dataIndex: "workorder_id",
        key: "workorder_id",
        render: (text, record) => (
          <div>
            <span>{text}</span>
            {record.is_retest === false && <span className="MarkSampleQCModal__MainWorkorderText">(Original)</span>}
          </div>
        ),
      },
      {
        title: "Genista ID",
        dataIndex: "genista_id",
        key: "genista_id",
        render: (text, record) => (
          <Tooltip placement="top" title={record.sample_id}>
            <span>{text}</span>
          </Tooltip>
        ),
      },
      {
        title: "Sample Type",
        dataIndex: "sample_type",
        key: "sample_type",
        render: (text) => text,
      },
      {
        title: "Test",
        dataIndex: "analysis",
        key: "analysis",
        render: (text) => text,
      },
      {
        title: (
          <div className="MarkSampleQCModal__ResultColumnHeader">
            <span>Result</span>
            {!readOnly && hasPickResultPermission && (
              !isPickResultMode ? (
                <Tooltip title={getPickResultTooltipMessage || undefined}>
                  <span>
                    <Button
                      type="link"
                      size="small"
                      onClick={handlePickResult}
                      disabled={!shouldShowPickResult}
                      className="MarkSampleQCModal__PickResultBtn"
                    >
                      Pick Result
                    </Button>
                  </span>
                </Tooltip>
              ) : (
                <div>
                  <Button
                    type="primary"
                    size="small"
                    onClick={handleSavePickResult}
                    disabled={!selectedWorkorderForReporting}
                    loading={resultUpdateLoading}
                    className="MarkSampleQCModal__SaveResultBtn"
                  >
                    Save
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    onClick={handleCancelPickResult}
                    disabled={resultUpdateLoading}
                    className="MarkSampleQCModal__CancelResultBtn"
                  >
                    Cancel
                  </Button>
                </div>
              )
            )}
          </div>
        ),
        dataIndex: "result",
        key: "result",
        width: 200,
        render: (text, record, index) => {
          const editState = workordersEditState[index] || {};
          const isEditing = !readOnly && editState.edit_result;
          const isRowPending = record.result_status && record.result_status.toLowerCase() === "pending";
          const canEditWorkorderResult = !readOnly && record.test_category !== "MRL" && record.result_status !== "Complete" && !isRowPending;
          const canPickResultForRow = !readOnly && hasPickResultPermission && isPickResultMode && !isRowPending;
          const showEditIcon = !readOnly && hasEditResultPermission && editState.showEditResult && !isEditing && !isPickResultMode && canEditWorkorderResult;

          // check edit_result property
          if (isEditing) {
            return (
              <div className="MarkSampleQCModal__ResultColumn">
                {resultTextArea(record, index, true)}
              </div>
            );
          }

          const hasResultActivity = Array.isArray(record.result_activity) && record.result_activity.length > 0;
          let resultTooltipTitle = null;
          if (hasResultActivity) {
            resultTooltipTitle = (
              <div className="MarkSampleQCModal__ResultTooltipContent">
                <div className="MarkSampleQCModal__ResultTooltipTitle">Previous Result</div>
                <ul>
                  {record.result_activity.map((activity, idx) => {
                    const dateTime = activity.date
                      ? moment(activity.date).format("MM/DD/YYYY, h:mm:ss a")
                      : "-";
                    const resultText = activity.result;
                    return (
                      <li key={idx}>
                        {`${dateTime} - ${resultText}`}
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          }

          const resultTextBlock = (
            <div className="MarkSampleQCModal__EditResult__Text">
              {hasPickResultPermission && isPickResultMode && (
                <Radio
                  checked={selectedWorkorderForReporting === record.key}
                  disabled={isRowPending}
                  onChange={() => !isRowPending && handleWorkorderSelectionForReporting(record.key)}
                  className="MarkSampleQCModal__ResultRadio"
                  onClick={(e) => e.stopPropagation()}
                />
              )}
              <span>{text || <>&nbsp;</>}</span>
            </div>
          );

          const resultContent = (
            <div
              className={`MarkSampleQCModal__ResultContent ${canPickResultForRow ? "MarkSampleQCModal__ResultContent--clickable" : ""} MarkSampleQCModal__EditResult`}
              onClick={canPickResultForRow ? () => handleWorkorderSelectionForReporting(record.key) : undefined}
              onMouseOver={() => {
                if (hasEditResultPermission && !isPickResultMode && canEditWorkorderResult) {
                  toggleWorkorderIcon(index, "enter", "showEditResult");
                }
              }}
              onFocus={() => null}
              onMouseLeave={() => {
                if (hasEditResultPermission && !isPickResultMode && canEditWorkorderResult) {
                  toggleWorkorderIcon(index, "leave", "showEditResult");
                }
              }}
            >
              {hasResultActivity && !isPickResultMode && resultTooltipTitle ? (
                <Tooltip placement="topLeft" title={resultTooltipTitle}>
                  <div className="MarkSampleQCModal__EditResultTextTrigger">
                    {resultTextBlock}
                  </div>
                </Tooltip>
              ) : (
                resultTextBlock
              )}
              {showEditIcon && (
                <div className="MarkSampleQCModal__ResultActionIcons">
                  <Tooltip title="Edit Result">
                    <EditOutlined
                      className="MarkSampleQCModal__EditIcon"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleEditWorkorderResult(index, text);
                      }}
                    />
                  </Tooltip>
                  <Tooltip title="Convert Unit">
                    <SwapOutlined
                      className="MarkSampleQCModal__ConvertUnitIcon"
                      onClick={(event) => {
                        event.stopPropagation();
                        setUnitConversionContext({
                          testName: record.analysis,
                          resultText: text == null ? "" : String(text),
                        });
                        setUnitConversionModalOpen(true);
                      }}
                    />
                  </Tooltip>
                </div>
              )}
            </div>
          );
          return (
            <div className="MarkSampleQCModal__ResultColumn">
              {resultContent}
            </div>
          );
        },
      },
      {
        title: "Status",
        dataIndex: "result_status",
        key: "result_status",
        render: (text) => {
          const key = text == null || String(text).trim() === "" ? "-" : String(text).trim();
          const statusConfig = BATCH_REPORT_TEST_STATUS[key];
          return (
            <span style={statusConfig.style}>{key}</span>
          );
        },
      },
      {
        title: "Report",
        dataIndex: "generate_status",
        key: "generate_status",
        render: (value) => {
          const key = (value == null || value === "") ? "null" : String(value).toLowerCase();
          const config = REPORT_STATUS_CONFIG[key];
          return (
            <span style={config.style}>{config.label}</span>
          );
        },
      },
      {
        title: "Manual QC",
        dataIndex: "manual_qc_result",
        key: "manual_qc_result",
        render: (_, record) => {
          const statusKey = record.manual_qc_result == null ? "-" : record.manual_qc_result;
          const flag = MANUAL_QC_FLAGS[statusKey];

          const hasTooltipContent = record.manual_qc_by
            || record.manual_qc_at
            || record.manual_qc_assigned_to_name;

          const tooltipTitle = hasTooltipContent ? (
            <div className="MarkSampleQCModal__TooltipPreLine">
              {record.manual_qc_by && `Updated By: ${record.manual_qc_by}\n`}
              {record.manual_qc_at
                && `Updated At: ${moment(record.manual_qc_at).format("MM/DD/YYYY, h:mm:ss a")}\n`}
              {record.manual_qc_assigned_to_name
                && `Assigned to: ${record.manual_qc_assigned_to_name}`}
            </div>
          ) : null;
          return (
            <Tooltip
              placement="top"
              title={tooltipTitle}
            >
              <span>
                <StatusTag
                  result={flag.value}
                  type="MANUAL_QC"
                />
              </span>
            </Tooltip>
          );
        },
      },
    ];
  }, [readOnly, selectedWorkorderForReporting, handleWorkorderSelectionForReporting, isPickResultMode, handlePickResult, handleCancelPickResult, handleSavePickResult, workordersData, hasUniqueResults, hasAnyPendingResult, hasPickResultPermission, hasEditResultPermission, getPickResultTooltipMessage, workordersEditState, handleEditWorkorderResult, toggleWorkorderIcon, resultTextArea, resultUpdateLoading]);

  const openRcaHistoryModal = useCallback(() => {
    setRcaHistoryModalOpen(true);
  }, []);

  /**
   * Render workorders table with expandable rows for related tests
   */
  const renderWorkordersTable = () => (
    <Table
      rowKey="key"
      columns={modalColumns}
      dataSource={workordersData}
      expandable={{
        expandedRowRender,
        rowExpandable: () => true, // Every row is expandable
      }}
      pagination={false}
      bordered
      size="middle"
    />
  );

  if (!rowData) {
    return (
      <Modal
        centered
        title="Quality Control"
        width={1300}
        open={open}
        onCancel={closeModal}
        rootClassName="MarkSampleQCModal"
        footer={null}
      >
        <div className="MarkSampleQCModal__LoadingWrapper">
          <Spin
            spinning
            size="large"
          />
        </div>
      </Modal>
    );
  }

  const pendingForText = rowData.on_dashboard_since;
  const hasActiveRca = rcaRecord?.rca_status === 1;
  const isRcaReadOnlyView = selectedResult === "4";
  // Submit stays enabled so users can click and get toast feedback for missing fields.
  // Only block when no QC result is selected (nothing to submit yet).
  const isSubmitDisabled = !selectedResult;

  return (
    <>
      <Modal
        centered
        title="Quality Control"
        width={1300}
        open={open}
        onCancel={closeModal}
        rootClassName="MarkSampleQCModal"
        footer={null}
      >
        <div className="MarkSampleQCModal__BodyContent">
          {showHistory ? (
            <HistoryView
              rowData={rowData}
              historyInfo={historyInfo}
              historyLoading={historyLoading}
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
              onBack={handleBackToQC}
            />
          ) : (
            <Spin spinning={loading || refetchRelatedInfoLoading} size="large">
              <div className="MarkSampleQCModal__HeaderActions">
                <div className="MarkSampleQCModal__HeaderActionsLeft">
                  {user.permissionMap.view_sample_qc_history && (
                  <Button
                    type="default"
                    className="MarkSampleQCModal__HistoryBtn"
                    onClick={handleShowHistory}
                  >
                    Sample QC History
                    <HistoryOutlined />
                  </Button>
                  )}
                  {user.permissionMap.view_sample_activity_qc && (
                  <Button
                    type="default"
                    className="MarkSampleQCModal__HistoryBtn"
                    onClick={() => setActivityModalOpen(true)}
                  >
                    Sample Activity
                  </Button>
                  )}
                </div>
              </div>
              <div className="MarkSampleQCModal__SectionWrapper">
                {/* <SampleInfoSection rowData={rowData} /> */}
                {renderWorkordersTable()}
              </div>
              {user.permissionMap.mark_qc_status && (
                <div className="MarkSampleQCModal__QCFormWrapper">
                  <QCFormSection
                    qcOptions={QC_OPTIONS}
                    selectedResult={selectedResult}
                    onQCOptionChange={onQCOptionChange}
                    note={note}
                    onNoteChange={(e) => setNote(e.target.value)}
                    retestComment={retestComment}
                    onRetestCommentChange={(e) => setRetestComment(e.target.value)}
                    textareaRow={textareaRow}
                    generateRetestDisableReason={generateRetestDisableReason}
                    genistaId={rowData.genista_id}
                    assignedTo={rowData.manual_qc_assigned_to_name}
                    testName={rowData.analysis}
                    pendingForText={pendingForText}
                    readOnly={readOnly}
                    retestReasonOptions={retestReasonOptions}
                    retestReasonsLoading={retestReasonsLoading}
                    retestReason={retestReason}
                    onRetestReasonChange={setRetestReason}
                    hasActiveRca={hasActiveRca}
                    expectedCompleteDate={expectedCompleteDate}
                    onExpectedCompleteDateChange={setExpectedCompleteDate}
                    pendingRcaS3Keys={pendingRcaS3Keys}
                    onPendingRcaS3KeysChange={setPendingRcaS3Keys}
                    completeRcaChecked={completeRcaChecked}
                    onCompleteRcaCheckedChange={setCompleteRcaChecked}
                    tisPk={rowData.tis_pk}
                    onRcaHistoryClick={rcaRecord ? openRcaHistoryModal : undefined}
                    rcaViewOnly={isRcaReadOnlyView}
                    displayRcaS3Keys={
                      Array.isArray(rcaRecord?.s3_keys) ? rcaRecord.s3_keys : pendingRcaS3Keys
                    }
                  />
                </div>
              )}
            </Spin>
          )}
        </div>
        {!showHistory && !readOnly && user.permissionMap.mark_qc_status && (
          <div className="MarkSampleQCModal__Footer">
            <Button
              key="pass"
              className="MarkSampleQCModal__SubmitBtn"
              loading={loading}
              disabled={isSubmitDisabled}
              type="primary"
              onClick={handleRcaSubmit}
            >
              Submit
            </Button>
          </div>
        )}
      </Modal>
      <ReportCommentModal
        open={reportCommentModalVisible}
        onCancel={() => {
          setSelectedActionOption(null);
          setReportCommentModalVisible(false);
        }}
        title={selectedActionOption ? `${getCommentModalTitle} — GB ID: ${selectedActionOption.element.genista_id}, WO ID: ${selectedActionOption.element.workorder_id}` : getCommentModalTitle}
        previousCommentTitle={selectedActionOption ? `${selectedActionOption.currentStatus} comment` : ""}
        previousComment={selectedActionOption ? selectedActionOption.element.ml_qc_comments : ""}
        onSubmit={({ data }) => updateApproveReject(data)}
        loading={approveRejectLoading}
        data={rowData}
        showQcDetail={false}
      />
      <ConfirmationModal
        open={showRetestConfirmationModal}
        onOk={() => closeRetestConfirmationModal()}
        showCancelButton={false}
        okText="Confirm"
        title="Retest Request Pending Approval"
        bodyText="Your retest request has been submitted. It's now waiting for approval. If approved, the retest will be generated automatically."
      />
      <ConfirmationModal
        open={specBlockReason}
        onOk={() => onMarkQC([], true)}
        onCancel={() => setSpecBlockReason(null)}
        showOkButton={user.permissionMap.force_pass_manual_qc === true}
        okText="Submit anyway"
        cancelText="Close"
        title="Confirmation Needed"
        bodyComponent={() => (
          <div>
            <span>Test is out of spec</span>
            <br />
            <span>{`Reason: ${specBlockReason}`}</span>
          </div>
        )}
      />
      <FilmImagePreviewModal
        open={filmPreviewModalOpen}
        onCancel={handleCloseFilmPreview}
        testData={currentTestData}
        onAnnotate={(annotationData) => {
          setAnnotateImagePath(annotationData);
          setAnnotateImageModalOpen(true);
        }}
      />
      <FapPdfPreviewModal
        open={resultPdfModalOpen}
        onCancel={handleCloseResultPdf}
        s3Info={currentResultPdfPreview.s3Info}
        title={currentResultPdfPreview.title}
      />
      {annotateImageModalOpen && (
        <AnnotateImageModal
          open={annotateImageModalOpen}
          onCancel={() => {
            setAnnotateImageModalOpen(false);
            setAnnotateImagePath(null);
          }}
          title={annotateImagePath ? `Annotate ${getAnnotationNameStem(annotateImagePath)}` : "Annotate image"}
          annotationPath={annotateImagePath}
        />
      )}
      {showPendingTestsModal && (
        <PendingTestsModal
          open
          onCancel={() => setShowPendingTestsModal(false)}
          tests={markSampleResponse.pending_tests}
          onSubmit={onMarkQC}
          loading={false}
        />
      )}
      <QcHistoryActivityModal
        open={activityModalOpen}
        onCancel={() => setActivityModalOpen(false)}
        record={rowData}
      />
      <RcaHistoryModal
        open={rcaHistoryModalOpen}
        onCancel={() => setRcaHistoryModalOpen(false)}
        tisPk={rowData?.tis_pk}
        workorderId={rowData?.workorder_id}
        genistaId={rowData?.genista_id}
        analysis={rowData?.analysis}
      />
      <UnitConversionModal
        open={unitConversionModalOpen}
        onCancel={() => {
          setUnitConversionModalOpen(false);
          setUnitConversionContext(null);
        }}
        testName={unitConversionContext?.testName}
        resultText={unitConversionContext?.resultText}
      />
    </>
  );
};

export default MarkSampleQCModal;
